import { motion } from "framer-motion";
import { ArrowRight, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES, COMPANY } from "@/lib/constants";

const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-charcoal-deep"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Rénovation intérieure haut de gamme à Genève par DEM BAT, entreprise familiale de plâtrerie et peinture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/65" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-40 sm:pt-44 pb-24 sm:pb-32 min-h-screen flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-4 mb-8 bg-black/35 backdrop-blur-sm py-3 px-5 sm:px-6 -ml-5 sm:-ml-6">
            <span className="h-px w-12 bg-white/80" />
            <span className="text-white text-xs uppercase tracking-[0.3em]">
              Entreprise familiale genevoise · {COMPANY.yearsExperience} ans
            </span>
          </div>

          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight font-normal drop-shadow-lg">
            <span className="sr-only">Entreprise de Rénovation à Genève. Plâtrerie, Peinture et Isolation par DEM BAT, famille Demiri à Grand-Lancy. </span>
            Votre espace mérite{" "}
            <span className="italic text-white/95 font-light">l'Excellence.</span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
            className="mt-10 text-white/85 text-lg sm:text-xl max-w-2xl font-light leading-relaxed"
          >
            Transformez votre appartement, votre maison ou votre local en un lieu dont vous serez fier.
            <br className="hidden sm:block" />
            Une famille genevoise, 20 ans de savoir-faire, et un seul artisan référent pour votre chantier.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.4 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <div className="flex flex-col gap-2">
              <a
                href="#devis"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 bg-bronze hover:bg-bronze-hover text-white px-8 py-5 text-xs uppercase tracking-[0.25em] transition-all duration-300"
              >
                Demander un devis gratuit
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </a>
              <span className="text-white/55 text-[11px] tracking-[0.2em] uppercase text-center sm:text-left">
                Réponse sous 24h · Sans engagement
              </span>
              <Link
                to="/candidatures"
                data-testid="hero-candidatures-link"
                className="mt-2 inline-flex items-center justify-center sm:justify-start gap-2 text-white/80 hover:text-white text-sm font-light transition-colors"
              >
                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                Vous cherchez un emploi ?{" "}
                <span className="underline underline-offset-4 decoration-white/60">Candidatures ici</span>
              </Link>
            </div>
            <a
              href="#realisations"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-8 py-5 text-xs uppercase tracking-[0.25em] transition-all duration-300 hover:bg-white/5 sm:self-start"
            >
              Voir nos réalisations
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.8 }}
          className="absolute bottom-10 left-6 sm:left-8 lg:left-12 right-6 sm:right-8 lg:right-12 flex items-end justify-between text-white/60 text-xs uppercase tracking-[0.25em]"
        >
          <span>Genève · Suisse</span>
          <span className="hidden sm:block">D · E · M</span>
          <span className="hidden md:block">Scroll ↓</span>
        </motion.div>
      </div>
    </section>
  );
};
